import React, {useState, useRef, useCallback, useEffect} from 'react';
import TextAreaAutosize from 'react-textarea-autosize';
import styled, {keyframes} from 'styled-components';
import {Avatar, TscAvatarSize, TscThemeName, TscThemeNames, TscStyles} from '@techsmith/tsc-cloud-style-guide';
import {useTranslation} from 'react-i18next';
import {PickerEmojiEvent} from '../../type/emoji-mart-data';
import config from '../../service/config';
import {StyledWarning} from '../util/StyledElements';
import Constants, {ButtonActivationKeys, AnnotationModes} from '../../constants/Constants';
import CommentSubmit from './CommentSubmit';
import {IWorkerState} from '../../model/workerState';
import {updateAnnotationManagerState, annotationManagerStore} from '../../context/annotationManagerProvider';
import withMemoizedContexts from '../../context/contextContainerHoC';
import EmojiPickerWrapper from './EmojiPickerWrapper';
import EditingCommentFooter from './EditingCommentFooter';
import {IComment} from '../../model/commentModel';
import {commentsStore, updateCommentsState} from '../../context/commentsProvider';
import LocalStorageWrapper from '../util/LocalStorageWrapper';
import {selectedConversationStore} from '../../context/selectedConversationProvider';
import {disableConversationStore} from '../../context/disableConversationProvider';
import {updateSelfIdentifiedUserProviderState} from '../../context/selfIdentifiedUserProvider';
import {useTscTheme} from '../../context/themeProvider';
import {mediaStore} from '../../context/mediaProvider';
import logging from '../../service/logging';
import {findEmojiByCode} from '../../util/EmojiSearch';
import {importMediaInterface} from '../../hooks/dynamicImport';

const focusAnimation = keyframes`
   0%   { box-shadow: ${TscStyles.color.action.primary} 0px 0px 0px 0px}
   50%   { box-shadow: ${TscStyles.color.action.primary} 0px 0px 0px 3px}
   100% { box-shadow: ${TscStyles.color.action.primary} 0px 0px 0px 1px}
`;

const CommentInputWrapper = styled.div<{isReply: boolean; theme: TscThemeNames; isEditing: boolean}>`
   margin-top: ${props => props.isReply && !props.isEditing ? '-8px' : '0'};
   margin-left: ${props => props.isReply && !props.isEditing ? '22px' : '0'};
   position: relative;
   padding: ${props => props.isReply ? '0 16px' : '0'};
   box-sizing: border-box;
   animation: fadeIn 350ms ease-in;
   @keyframes fadeIn {
      0% { opacity: 0;}
      100% { opacity: 1; }
   }
`;

const AvatarWrapper = styled.div<{isReply: boolean}>`
   z-index: 2;
   margin-top: 4px;
`;

export const TextAreaWrapper = styled.div<{isReply: boolean; isEditing: boolean}>`
   width: ${props => props.isReply ? 'auto' : '100%'};
   display: flex;
   align-items: flex-start;
   box-sizing: border-box;
   margin-top: ${props => props.isReply && props.isEditing ? '12px' : '0'};
   margin-bottom: ${props => !props.isEditing ? '8px' : '0'};
   position: relative;
`;

export const StyledTextArea = styled(TextAreaAutosize) <{$hasError: boolean; $theme: TscThemeNames; $isReply: boolean; $isEditing: boolean}>`
   border-color: ${props => props.$hasError ? `${TscStyles.color.action.destructive} !important` : null};
   margin-left: ${props => props.$isEditing || (config.user.TechSmithId === Constants.nullTechSmithId) ? '0' : '8px'};
   font-size: 0.875rem;
   padding: 6px 30px 6px 8px;
   flex: 1 1 auto;
   color: ${props => props.$theme === TscThemeName.dusk ? TscStyles.color.text.light : TscStyles.color.text.dark};
   background-color: ${props =>
         props.$isReply
            ? props.$theme === TscThemeName.dusk ? TscStyles.color.ui.dusk.dark
               : TscStyles.color.ui.dawn.light
            : props.$theme === TscThemeName.dusk && props.$isEditing ? TscStyles.color.ui.dusk.mediumDark
               : props.$theme === TscThemeName.dawn && props.$isEditing ? TscStyles.color.ui.dawn.light
                  : props.$theme === TscThemeName.dusk ? TscStyles.color.ui.dusk.mediumDark
                     : TscStyles.color.ui.dawn.lightMedium
   };
   font-weight: 400;
   border-radius: ${TscStyles.border.radius.md};
   border: 1px solid ${props => props.$theme === TscThemeName.dusk ? TscStyles.color.action.secondaryDusk : TscStyles.color.ui.dawn.medium};
   resize: none;
   -ms-overflow-y: hidden !important; //Needed, else IE gives scrollbar
   font-family: inherit;

   &:focus {
      border-color: ${props => props.$hasError ? TscStyles.color.action.destructive : TscStyles.color.action.primary};
      outline: none;
      animation: ${focusAnimation} 500ms ease;
      animation-fill-mode: forwards;
      box-shadow: 0px 0px 0px 1px ${TscStyles.color.action.primary};
   }

   &::placeholder {
      color: ${props => props.$theme === TscThemeName.dusk ? TscStyles.color.text.lightMedium : TscStyles.color.text.medium};
      opacity: 1;
   }
`;

export const CommentInputError = styled(StyledWarning)`
   margin-left: 49px;
`;

export const CommentInputBase: React.FC<ICommentInputProps & IStateMappedProps> = ({hasAnnotation, comments, isCommentOwner, isReply, comment, inputId, deleteAnnotationOnSave, annotationMode, loadingState, VideoReviewId, isCommentingDisabled, onSubmit, setShowDeleteAlertModal, setDeleteAnnotationOnSave, IsVideo}) => {
   const {t} = useTranslation();
   const theme = useTscTheme();
   const [annotationExists, setAnnotationExists] = useState(hasAnnotation);
   const [text, setText] = useState(comment?.text || '');
   const [textSelectionPosition, setTextSelectionPosition] = useState(0);
   const textAreaRef = useRef<HTMLTextAreaElement>(null);
   const textLengthError: boolean = text.length > Constants.maxCommentLength;
   const disableSubmit: boolean = loadingState.isWorking || !text.trim() && (!annotationExists || annotationMode !== AnnotationModes.edit) || (loadingState.hasError || textLengthError);
   const isTextOverflow = textAreaRef?.current?.style?.overflow !== 'hidden';

   useEffect(() => {
      const savedText = LocalStorageWrapper.getItem(Constants.localStorageKeys.unsavedCommentText.replace('{videoReviewId}', VideoReviewId).replace('{inputId}', inputId ? inputId : null));
      savedText && setText(savedText);

      return () => {
         LocalStorageWrapper.setItem(Constants.localStorageKeys.unsavedCommentText.replace('{videoReviewId}', VideoReviewId).replace('{inputId}', inputId ? inputId : null), textAreaRef.current?.value || '');
         setText('');
      };
   }, [VideoReviewId]);

   useEffect(() => {
      comment?.text && setText(comment.text);
   }, [comment?.text]);

   const replaceEmojiCodeWithEmoji = (searchText: string, emojiCode: string) => {
      const nativeEmoji = findEmojiByCode(emojiCode);
      if (nativeEmoji) {
         setText(searchText.replace(emojiCode, nativeEmoji));
      }
   };

   const textToEmoji = (searchText: string) => {
      try {
         const foundEmojiCodes = searchText.match(/:[^\s:]+:/g);
         foundEmojiCodes?.forEach(emojiCode => {
            replaceEmojiCodeWithEmoji(searchText, emojiCode);
         });
      } catch (e) {
         logging.error('Failed to convert text to emoji: ', e);
      }
   };

   const onKeyPress = (e: React.KeyboardEvent) => {
      if (e.key === ButtonActivationKeys.enter && !e.shiftKey) {
         e.stopPropagation();
         e.preventDefault();

         if (text.trim().length) {
            onSubmitClicked();
         }
      }
   };

   const onKeyUp = () => {
      setTextSelectionPosition(textAreaRef.current.selectionStart);
   };

   const onInputClick = () => {
      setTextSelectionPosition(textAreaRef.current.selectionStart);
   };

   const insertOrAppendText = (originalText: string, textToAdd: string, position?: number): string => {
      if (typeof position === 'number') {
         return `${originalText.slice(0, position)}${textToAdd}${originalText.slice(position)}`;
      }
      return `${originalText}${textToAdd}`;
   };

   const onEmojiClick = (emoji: PickerEmojiEvent) => {
      setText(insertOrAppendText(textAreaRef.current.value, emoji.native, textSelectionPosition));
   };

   const memoizedOnEmojiClick = useCallback(onEmojiClick, [textAreaRef.current, textSelectionPosition]);

   const onUpdateText = () => {
      setText(textAreaRef.current.value);
      textToEmoji(textAreaRef.current.value);
   };

   const getErrorText = (): string => {
      if (textLengthError) {
         return t('conversations.commentTooLongErrorText', {overByCount: text.length - Constants.maxCommentLength});
      }
      return t('general.errorText');
   };

   const onSubmitClicked = () => {
      setAnnotationExists(false);
      onSubmit(text);
      setText('');
      LocalStorageWrapper.removeItem(Constants.localStorageKeys.unsavedCommentText.replace('{videoReviewId}', VideoReviewId).replace('{inputId}', inputId ? inputId : null));
   };

   const handleCancelEdit = () => {
      if (isCommentOwner) {
         textAreaRef.current.value = comment.text;
         setText(comment.text);
      }

      const commentsCopy = Object.assign([] as IComment[], comments);

      if (isReply) {
         updateCommentsState({
            comments: commentsCopy.map(c => {
               if (c.id !== comment.parentCommentId) {
                  return c;
               }
               return Object.assign({}, c, {replies: c.replies.map((r: IComment) => r.id === comment.id ? Object.assign({}, r, {isEditing: false}) : r)});
            })
         });
      } else {
         updateCommentsState({
            comments: commentsCopy.map(c => {
               if (c.id === comment.id) {
                  return Object.assign({}, c, {isEditing: false});
               }
               return c;
            })
         });
      }

      deleteAnnotationOnSave && setDeleteAnnotationOnSave(false);
   };

   const onTextInputFocus = async () => {
      const mediaInterface = await importMediaInterface();

      IsVideo && mediaInterface.pause();

      if (annotationMode === AnnotationModes.display && !isReply && !comment?.isEditing) {
         updateAnnotationManagerState({annotationMode: AnnotationModes.none});
      }

      if (config.user.TechSmithId === Constants.nullTechSmithId) {
         updateSelfIdentifiedUserProviderState({showSelfIdentifyModal: true});
      }
   };

   return (
      <CommentInputWrapper
         isReply={isReply}
         theme={theme}
         isEditing={comment?.isEditing}
      >
         {(isCommentOwner || !comment?.isEditing) && (
            <TextAreaWrapper
               isReply={isReply}
               isEditing={comment?.isEditing}
            >
               {!comment?.isEditing && config.user.TechSmithId !== Constants.nullTechSmithId && (
                  <AvatarWrapper isReply={isReply}>
                     <Avatar
                        avatarImageUrl={config.user.AvatarImageUrl}
                        userDisplayName={config.user.DisplayName}
                        userAltText={null}
                        userID={config.user.TechSmithId}
                        size={TscAvatarSize.small}
                     />
                  </AvatarWrapper>
               )}
               <StyledTextArea
                  id={inputId}
                  aria-label={comment?.isEditing ? '' : isReply ? t('conversations.replyPlaceholder') : t('conversations.commentPlaceholder')}
                  placeholder={comment?.isEditing ? '' : isReply ? t('conversations.replyPlaceholder') : t('conversations.commentPlaceholder')}
                  value={text}
                  disabled={isCommentingDisabled}
                  onChange={onUpdateText}
                  onKeyPress={onKeyPress}
                  onKeyUp={onKeyUp}
                  onClick={onInputClick}
                  maxRows={10}
                  ref={textAreaRef}
                  onFocus={onTextInputFocus}
                  data-test-id="conversation-comment-input-text"
                  $hasError={loadingState.hasError || textLengthError}
                  $isReply={isReply}
                  $isEditing={comment?.isEditing}
                  $theme={theme}
               />
               {config.user.TechSmithId !== Constants.nullTechSmithId && (
                  <EmojiPickerWrapper
                     isEditing={comment?.isEditing}
                     isTextOverflow={isTextOverflow}
                     theme={theme}
                     onEmojiClick={memoizedOnEmojiClick}
                     data-test-id="emoji-picker"
                  />
               )}
            </TextAreaWrapper>
         )}
         {loadingState.hasError || textLengthError && (
            <CommentInputError>
               {getErrorText()}
            </CommentInputError>
         )}
         {comment?.isEditing ? (
            <EditingCommentFooter
               isCommentOwner={isCommentOwner}
               isReply={isReply}
               comment={comment}
               editedText={text}
               onCancel={handleCancelEdit}
               setShowDeleteAlertModal={setShowDeleteAlertModal}
               onSubmit={onSubmitClicked}
               deleteAnnotationOnSave={deleteAnnotationOnSave}
            />
         ) : (
            <CommentSubmit
               setAnnotationExists={setAnnotationExists}
               isReply={isReply}
               onSubmit={onSubmitClicked}
               isDisabled={disableSubmit}
            />
         )}
      </CommentInputWrapper>
   );
};

export interface ICommentInputProps {
   hasAnnotation: boolean;
   isReply: boolean;
   loadingState: IWorkerState;
   isCommentOwner: boolean;
   deleteAnnotationOnSave?: boolean;
   comment?: IComment;
   inputId?: string;
   onSubmit(commentText: string): void;
   setShowDeleteAlertModal?(showDeleteAlertModal: boolean): void;
   setDeleteAnnotationOnSave?(deleteAnnotationOnSave: boolean): void;
}

export interface IStateMappedProps {
   annotationMode: AnnotationModes;
   comments: IComment[];
   VideoReviewId: string;
   isCommentingDisabled: boolean;
   IsVideo: boolean;
}

const mapStatesToProps = (
   {isReply, loadingState, isCommentOwner, deleteAnnotationOnSave, comment, inputId, hasAnnotation, onSubmit, setShowDeleteAlertModal, setDeleteAnnotationOnSave}: ICommentInputProps,
   {annotationMode}: Partial<IStateMappedProps>,
   {comments}: Partial<IStateMappedProps>,
   {VideoReviewId}: Partial<IStateMappedProps>,
   {isCommentingDisabled}: Partial<IStateMappedProps>,
   {IsVideo}: Partial<IStateMappedProps>
): ICommentInputProps & IStateMappedProps => ({hasAnnotation, comments, isReply, loadingState, isCommentOwner, deleteAnnotationOnSave, comment, inputId, annotationMode, VideoReviewId, isCommentingDisabled, onSubmit, setShowDeleteAlertModal, setDeleteAnnotationOnSave, IsVideo});

export default withMemoizedContexts(mapStatesToProps, annotationManagerStore, commentsStore, selectedConversationStore, disableConversationStore, mediaStore)(CommentInputBase);
